import { GlobalNumberRequest, Ticket } from "callable-data-objects";
import useCheckRole from "Hooks/APICheckRole";
import moment from "moment";
import APIAccountProvider from "Providers/APIAccountProvider";
import React, { useState } from "react";
import {
  Breadcrumb,
  Container,
  Grid,
  Menu,
  Segment,
  Table,
} from "semantic-ui-react";
import AllocationBuildForm from "./AllocationBuildForm";
import AllocationDetails from "./AllocationDetails";
import AssignmentDetails from "./AssignmentDetails";
import AssignmentReferenceForm from "./AssignmentReferenceForm";
import ExportBuildForm from "./ExportBuildForm";
import ExportDetails from "./ExportDetails";
import GlobalNumberBuildForm from "./GlobalNumberBuildForm";
import GlobalNumberDetails from "./GlobalNumberDetails";
import MigrationBuildForm from "./MigrationBuildForm";
import PortingBuildForm from "./PortingBuildForm";
import PortingDetails from "./PortingDetails";
import TicketAccountEvents from "./TicketAccountEvents";
import TicketComment from "./TicketComment";
import TicketNote from "./TicketNote";
import TicketRecipients from "./TicketRecipients";
import TicketStatusLabel from "./TicketStatusLabel";
import TicketTypeLabel from "./TicketTypeLabel";

type TicketDetailProps = {
  ticket: Ticket;
  unselect: Function;
  closeTicket: Function;
  openTicket: Function;
  pendingTicket: Function;
  updateEmails: Function;
  addComment: Function;
  addNote: Function;
};

const getTicketPayload = (ticket: Ticket) => {
  if (ticket.type === "port-request") {
    return ticket.portRequest;
  } else if (ticket.type === "export-request") {
    return ticket.exportRequest;
  } else if (ticket.type === "allocation-request") {
    return ticket.allocationRequest;
  } else if (ticket.type === "assignment-request") {
    return ticket.assignmentRequest;
  } else if (ticket.type === "migration-request") {
    return ticket.migrationRequest;
  } else if (ticket.type === "global-number-request") {
    return ticket.globalNumberRequest;
  }
  throw new Error("Unkown payload Type");
};

const getAccount = (ticket: Ticket) => {
  if (ticket.type === "port-request") {
    return ticket.portRequest.customer;
  } else if (ticket.type === "export-request") {
    return ticket.exportRequest.customer;
  } else if (ticket.type === "allocation-request") {
    return ticket.allocationRequest.customer;
  } else if (ticket.type === "assignment-request") {
    return ticket.assignmentRequest.customer;
  } else if (ticket.type === "migration-request") {
    return ticket.migrationRequest.customer;
  } else if (ticket.type === "global-number-request") {
    return undefined;
  }
  throw new Error("Unkown payload Type = " + ticket.type);
};

const TicketDetail = ({
  ticket,
  unselect,
  closeTicket,
  openTicket,
  pendingTicket,
  updateEmails,
  addComment,
  addNote,
}: TicketDetailProps) => {
  const { hasRole, roles } = useCheckRole();

  const canEdit = hasRole(roles.ticket.roles.edit);
  const canViewBilling = hasRole(roles.ticket.billing.view);
  const canViewProvisioning = hasRole(roles.ticket.build.view);
  const canViewSubmissions = hasRole(roles.ticket.submissions.view);
  const canViewAssets = hasRole(roles.ticket.asset.view);
  const canComment = hasRole(roles.ticket.roles.comment);
  const canNote = hasRole(roles.ticket.roles.note);

  const canViewSubView = canEdit || canViewBilling || canViewProvisioning;
  const canViewPorting = canViewSubView || canViewSubmissions || canViewAssets;
  const canViewExport = canViewSubView;
  const canViewAllocation = canViewSubView;

  const getTicketType = (ticket: Ticket): string => {
    if (ticket.type === "port-request") {
      if (canViewPorting) return "Porting";
      if (canComment) return "Comments";
      if (canNote) return "Notes";
    } else if (ticket.type === "export-request") {
      if (canViewExport) return "Export";
      if (canComment) return "Comments";
      if (canNote) return "Notes";
    } else if (ticket.type === "allocation-request") {
      if (canViewAllocation) return "Allocation";
      if (canComment) return "Comments";
      if (canNote) return "Notes";
    } else if (ticket.type === "assignment-request") {
      if (canEdit) return "Assignment";
      if (canComment) return "Comments";
      if (canNote) return "Notes";
    } else if (ticket.type === "migration-request") {
      if (canEdit) return "Migration";
      if (canComment) return "Comments";
      if (canNote) return "Notes";
    } else if (ticket.type === "global-number-request") {
      if (canEdit) return "GlobalNumber";
      if (canComment) return "Comments";
      if (canNote) return "Notes";
    }
    return "Unknown";
  };

  const [activeView, setActiveView] = useState<string>(getTicketType(ticket));
  const payload = getTicketPayload(ticket);
  const accountRef = getAccount(ticket);

  return (
    <Container fluid>
      <Menu pointing>
        <Menu.Item>
          <Breadcrumb>
            <Breadcrumb.Section link onClick={() => unselect()}>
              Tickets
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section>{getTicketType(ticket)}</Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section>{ticket.caseId}</Breadcrumb.Section>
          </Breadcrumb>
        </Menu.Item>
        <Menu.Menu position='right'>
          <Menu.Item
            disabled={!canEdit}
            onClick={() => openTicket()}
            name='Open'
            active={ticket.status === "open"}
          />
          <Menu.Item
            disabled={!canEdit}
            onClick={() => pendingTicket()}
            name='Pending'
            active={ticket.status === "pending"}
          />
          <Menu.Item
            disabled={!canEdit}
            onClick={() => closeTicket()}
            name='Closed'
            active={ticket.status === "closed"}
          />
        </Menu.Menu>
      </Menu>
      <Grid padded stackable>
        <Grid.Column width={4}>
          <TicketRecipients ticket={ticket} updateEmails={updateEmails} />
          <Table size='small' padded={false} singleLine basic stackable>
            <Table.Body>
              <Table.Row>
                <Table.Cell>Ticket ID</Table.Cell>
                <Table.Cell>{ticket.caseId}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Status</Table.Cell>
                <Table.Cell>
                  <TicketStatusLabel ticket={ticket} />
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Type</Table.Cell>
                <Table.Cell>
                  <TicketTypeLabel ticket={ticket} />
                </Table.Cell>
              </Table.Row>
              {!(payload instanceof GlobalNumberRequest) && (
                <Table.Row>
                  <Table.Cell>Customer</Table.Cell>
                  <Table.Cell>{payload?.customer?.name}</Table.Cell>
                </Table.Row>
              )}
              <Table.Row>
                <Table.Cell>Created</Table.Cell>
                <Table.Cell>
                  {moment(ticket.created).format("DD/MM/YYYY hh:mm")}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
          {ticket.type === "export-request" && (
            <ExportDetails ticket={ticket} />
          )}
          {ticket.type === "assignment-request" && (
            <AssignmentDetails ticket={ticket} />
          )}
          {ticket.type === "allocation-request" && (
            <AllocationDetails ticket={ticket} />
          )}
          {ticket.type === "global-number-request" && (
            <GlobalNumberDetails ticket={ticket} />
          )}
          {ticket.type === "port-request" && <PortingDetails ticket={ticket} />}
        </Grid.Column>
        <Grid.Column width={12}>
          <Menu pointing stackable>
            {ticket.type === "export-request" && canViewExport && (
              <Menu.Item
                active={activeView === "Export"}
                name='Export'
                onClick={() => setActiveView("Export")}
              />
            )}
            {ticket.type === "allocation-request" && canViewAllocation && (
              <Menu.Item
                active={activeView === "Allocation"}
                name='Allocation'
                onClick={() => setActiveView("Allocation")}
              />
            )}
            {ticket.type === "assignment-request" && canEdit && (
              <Menu.Item
                active={activeView === "Assignment"}
                name='Reference Details'
                onClick={() => setActiveView("Assignment")}
              />
            )}
            {ticket.type === "port-request" && canViewPorting && (
              <Menu.Item
                active={activeView === "Porting"}
                name='Porting'
                onClick={() => setActiveView("Porting")}
              />
            )}
            {ticket.type === "global-number-request" && canEdit && (
              <Menu.Item
                active={activeView === "GlobalNumber"}
                name='Global Number Details'
                onClick={() => setActiveView("GlobalNumber")}
              />
            )}
            {ticket.type === "migration-request" && canEdit && (
              <Menu.Item
                active={activeView === "Migration"}
                name='Migration Details'
                onClick={() => setActiveView("Migration")}
              />
            )}
            {canComment && (
              <Menu.Item
                active={activeView === "Comments"}
                name='Comments'
                onClick={() => setActiveView("Comments")}
              />
            )}
            {canNote && (
              <Menu.Item
                active={activeView === "Notes"}
                name='Private Notes'
                onClick={() => setActiveView("Notes")}
              />
            )}
            {accountRef && canEdit && (
              <Menu.Item
                active={activeView === "Events"}
                name='Events'
                onClick={() => setActiveView("Events")}
              />
            )}
          </Menu>
          <Container fluid>
            <Segment>
              {activeView === "Assignment" && <AssignmentReferenceForm />}
              {activeView === "Porting" && <PortingBuildForm />}
              {activeView === "Migration" && <MigrationBuildForm />}
              {activeView === "Allocation" && <AllocationBuildForm />}
              {activeView === "GlobalNumber" && <GlobalNumberBuildForm />}
              {activeView === "Export" && <ExportBuildForm />}
              {activeView === "Comments" && (
                <TicketComment ticket={ticket} addComment={addComment} />
              )}
              {activeView === "Notes" && (
                <TicketNote ticket={ticket} addNote={addNote} />
              )}

              {activeView === "Events" && accountRef && (
                <APIAccountProvider>
                  <TicketAccountEvents
                    ticket={ticket}
                    accountRef={accountRef}
                  />
                </APIAccountProvider>
              )}
            </Segment>
          </Container>
        </Grid.Column>
      </Grid>
    </Container>
  );
};

export default TicketDetail;
